import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

/* ── Button style helper ─────────────────────────────────────── */
const btn = (disabled = false) => ({
  all: "unset",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  borderRadius: "7px",
  background: disabled ? "transparent" : "rgba(255,255,255,0.06)",
  color: disabled ? "rgba(139,157,195,0.3)" : "#c4d0ea",
  cursor: disabled ? "default" : "pointer",
  flexShrink: 0,
  transition: "background 0.15s, color 0.15s",
  WebkitTapHighlightColor: "transparent",
});

/* ── SVG icons ───────────────────────────────────────────────── */
const IconZoomOut = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

const IconZoomIn = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    <line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

const IconFullscreen = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3" />
    <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
    <path d="M3 16v3a2 2 0 0 0 2 2h3" />
    <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
  </svg>
);

const IconExitFullscreen = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3" />
    <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
    <path d="M3 16h3a2 2 0 0 1 2 2v3" />
    <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
  </svg>
);

/* ── Component ───────────────────────────────────────────────── */
const SharedScreenVideo = ({ userId, remoteStream, screenShardName }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Refs shared with passive event handlers (no stale-closure issue)
  const stateRef = useRef({ scale: 1, pan: { x: 0, y: 0 } });
  const touchRef = useRef({ pinching: false, lastDist: 0, dragging: false, lastX: 0, lastY: 0 });
  const mouseRef = useRef({ down: false, lastX: 0, lastY: 0 });

  // Keep stateRef current
  useEffect(() => { stateRef.current = { scale, pan }; }, [scale, pan]);

  /* ── Video source ──────────────────────────────────────────── */
  useEffect(() => {
    if (videoRef.current && remoteStream) {
      videoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  /* ── Fullscreen listener ───────────────────────────────────── */
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  /* ── Touch events (passive:false required for preventDefault) ─ */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        touchRef.current.pinching = true;
        touchRef.current.dragging = false;
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        touchRef.current.lastDist = Math.hypot(dx, dy);
      } else if (e.touches.length === 1) {
        touchRef.current.dragging = true;
        touchRef.current.pinching = false;
        touchRef.current.lastX = e.touches[0].clientX;
        touchRef.current.lastY = e.touches[0].clientY;
      }
    };

    const onTouchMove = (e) => {
      e.preventDefault(); // must be non-passive
      const { scale: s, pan: p } = stateRef.current;

      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        if (touchRef.current.lastDist > 0) {
          const ratio = dist / touchRef.current.lastDist;
          const next = Math.min(5, Math.max(1, s * ratio));
          setScale(next);
          if (next <= 1) setPan({ x: 0, y: 0 });
        }
        touchRef.current.lastDist = dist;
      } else if (e.touches.length === 1 && touchRef.current.dragging && s > 1) {
        const dx = e.touches[0].clientX - touchRef.current.lastX;
        const dy = e.touches[0].clientY - touchRef.current.lastY;
        setPan({ x: p.x + dx, y: p.y + dy });
        touchRef.current.lastX = e.touches[0].clientX;
        touchRef.current.lastY = e.touches[0].clientY;
      }
    };

    const onTouchEnd = (e) => {
      if (e.touches.length < 2) { touchRef.current.pinching = false; touchRef.current.lastDist = 0; }
      if (e.touches.length === 0) touchRef.current.dragging = false;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, []); // stateRef always current — no deps needed

  /* ── Wheel zoom (zoom toward cursor) ───────────────────────── */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mouseX = e.clientX - rect.left - rect.width / 2;
      const mouseY = e.clientY - rect.top - rect.height / 2;
      const factor = e.deltaY < 0 ? 1.15 : 0.87;
      setScale(s => {
        const next = Math.min(5, Math.max(1, s * factor));
        if (next <= 1) { setPan({ x: 0, y: 0 }); return 1; }
        const ratio = next / s;
        setPan(p => ({
          x: mouseX + ratio * (p.x - mouseX),
          y: mouseY + ratio * (p.y - mouseY),
        }));
        return next;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  /* ── Mouse drag (desktop) ──────────────────────────────────── */
  const onMouseDown = (e) => {
    if (scale <= 1) return;
    mouseRef.current = { down: true, lastX: e.clientX, lastY: e.clientY };
    setIsDragging(true);
  };
  const onMouseMove = (e) => {
    if (!mouseRef.current.down) return;
    const dx = e.clientX - mouseRef.current.lastX;
    const dy = e.clientY - mouseRef.current.lastY;
    setPan(p => ({ x: p.x + dx, y: p.y + dy }));
    mouseRef.current.lastX = e.clientX;
    mouseRef.current.lastY = e.clientY;
  };
  const onMouseUp = () => { mouseRef.current.down = false; setIsDragging(false); };

  /* ── Zoom controls ─────────────────────────────────────────── */
  const zoomIn = (e) => {
    e.stopPropagation();
    setScale(s => Math.min(5, +(s + 0.5).toFixed(1)));
  };
  const zoomOut = (e) => {
    e.stopPropagation();
    setScale(s => {
      const next = Math.max(1, +(s - 0.5).toFixed(1));
      if (next <= 1) setPan({ x: 0, y: 0 });
      return next;
    });
  };
  const resetZoom = (e) => {
    e.stopPropagation();
    setScale(1);
    setPan({ x: 0, y: 0 });
  };

  /* ── Fullscreen ────────────────────────────────────────────── */
  const toggleFullscreen = (e) => {
    e.stopPropagation();
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const isZoomed = scale > 1.01;

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        borderRadius: "12px",
        background: "#060a14",
        border: "1px solid #1a2540",
        cursor: isZoomed ? (isDragging ? "grabbing" : "grab") : "default",
        userSelect: "none",
        WebkitUserSelect: "none",
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      {/* Video */}
      <video
        ref={videoRef}
        id={`videoX_${userId}`}
        autoPlay
        playsInline
        data-username={userId}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
          transformOrigin: "center center",
          willChange: "transform",
          pointerEvents: "none",
        }}
      />

      {/* Screen share label */}
      {screenShardName && (
        <div style={{
          position: "absolute",
          top: "12px",
          left: "12px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "5px 14px 5px 10px",
          background: "rgba(8, 13, 28, 0.85)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "1px solid #1a2540",
          borderRadius: "20px",
          fontFamily: "'Inter', system-ui, sans-serif",
          pointerEvents: "none",
          zIndex: 5,
        }}>
          <span style={{
            width: "7px", height: "7px", borderRadius: "50%",
            background: "#22c55e", flexShrink: 0,
            animation: "ssBlink 2s ease-in-out infinite",
          }} />
          <span style={{
            fontSize: "12.5px", fontWeight: "600",
            color: "#c4d0ea", whiteSpace: "nowrap", letterSpacing: "0.01em",
          }}>
            {screenShardName} - sharing screen
          </span>
        </div>
      )}

      {/* Pan hint when zoomed */}
      {isZoomed && (
        <div style={{
          position: "absolute",
          bottom: "12px",
          left: "12px",
          fontSize: "10px",
          color: "rgba(196,208,234,0.65)",
          background: "rgba(8,13,28,0.75)",
          padding: "3px 9px",
          borderRadius: "8px",
          pointerEvents: "none",
          fontFamily: "'Inter', system-ui, sans-serif",
          zIndex: 5,
        }}>
          {window.ontouchstart !== undefined ? "Pinch · drag to pan" : "Scroll · drag to pan"}
        </div>
      )}

      {/* Controls toolbar */}
      <div
        style={{
          position: "absolute",
          bottom: "12px",
          right: "12px",
          display: "flex",
          alignItems: "center",
          gap: "3px",
          background: "rgba(8, 13, 28, 0.88)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.09)",
          borderRadius: "10px",
          padding: "4px 6px",
          zIndex: 10,
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
        onMouseDown={e => e.stopPropagation()}
      >
        {/* Zoom out */}
        <button onClick={zoomOut} disabled={!isZoomed} style={btn(!isZoomed)} title="Zoom out">
          <IconZoomOut />
        </button>

        {/* Zoom level — click to reset */}
        <button
          onClick={resetZoom}
          title="Reset zoom"
          style={{
            ...btn(false),
            width: "auto",
            padding: "0 6px",
            fontSize: "11px",
            fontWeight: "600",
            letterSpacing: "0.01em",
            minWidth: "36px",
            color: isZoomed ? "#22d3ee" : "#8b9dc3",
            background: isZoomed ? "rgba(34,211,238,0.1)" : "rgba(255,255,255,0.04)",
          }}
        >
          {Math.round(scale * 100)}%
        </button>

        {/* Zoom in */}
        <button onClick={zoomIn} disabled={scale >= 5} style={btn(scale >= 5)} title="Zoom in">
          <IconZoomIn />
        </button>

        {/* Divider */}
        <div style={{ width: "1px", height: "16px", background: "rgba(255,255,255,0.1)", margin: "0 3px" }} />

        {/* Fullscreen */}
        <button onClick={toggleFullscreen} style={btn(false)} title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
          {isFullscreen ? <IconExitFullscreen /> : <IconFullscreen />}
        </button>
      </div>

      <style>{`
        @keyframes ssBlink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.35; }
        }
      `}</style>
    </div>
  );
};

SharedScreenVideo.propTypes = {
  userId: PropTypes.any,
  remoteStream: PropTypes.any,
  screenShardName: PropTypes.any,
};

export default SharedScreenVideo;